import React, {
  FC,
  MouseEvent,
  MouseEventHandler,
  ReactElement,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Col, Row } from "react-bootstrap";
import { toast } from "react-toastify";
import FormInput from "../../../../components/Form/components/FormInput";
import { FormContext } from "../../../../components/Form/models/FormContext";
import Modal from "../../../../components/Modal";
import useLoading from "../../../../hooks/useLoading";
import FormSection from "../../../../models/salesforce/FormSection";
import FormSectionService from "../../../../services/forms/FormSectionService";

export type FormSectionAddModalProps = {
  id: string;
  show: boolean;
  onCancel: MouseEventHandler<HTMLButtonElement>;
  onSubmit: (formSection: FormSection) => void;
  formId: string;
  order: number;
};

const FormSectionAddModal: FC<FormSectionAddModalProps> = ({
  id,
  show,
  onCancel,
  onSubmit,
  formId,
  order,
}: FormSectionAddModalProps): ReactElement => {
  const [formSection, setFormSection] = useState<FormSection>({});
  const loading = useLoading();
  const initialRequest = useMemo(() => ({}), []);

  const handleSubmit = async (event: MouseEvent<HTMLButtonElement>) => {
    loading(async () => {
      try {
        event.preventDefault();
        event.stopPropagation();
        const responses = await FormSectionService.saveFormSection(formSection);
        if (responses.every(({ success }) => success)) {
          onSubmit({
            ...formSection,
            id: responses[0]?.id,
          });
        } else {
          [
            {
              message: "Failed to create form section",
            },
            ...responses.flatMap(({ errors }) => errors || []),
          ].forEach(({ message }) => toast.error(message));
        }
      } catch {
        toast.error("Failed to create form section");
      }
    });
  };

  useEffect(() => {
    setFormSection((previousFormSection) => ({
      ...previousFormSection,
      formId,
      order,
    }));
  }, [formId, order]);

  return (
    <Modal
      id={id}
      show={show}
      title="Add Form Section"
      body={
        <>
          <FormContext.Provider
            value={{
              initialRequest,
              setRequest: setFormSection,
              disabled: false,
            }}
          >
            <Row>
              <Col className="mb-3">
                <FormInput
                  id="add-form-section-name"
                  name="name"
                  label="Name"
                  type="text"
                  required
                />
                <FormInput
                  id="add-form-section-description"
                  name="description"
                  label="Description"
                  type="text"
                />
              </Col>
            </Row>
          </FormContext.Provider>
        </>
      }
      onCancel={onCancel}
      onSubmit={handleSubmit}
    />
  );
};

export default FormSectionAddModal;
